import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { motion } from "framer-motion";
import { ArrowLeft, CreditCard, Scale, Wallet, HelpCircle } from "lucide-react";

const SECTIONS = [
  {
    id: "betaling",
    icon: <CreditCard />,
    color: "bg-primary/10 text-primary",
    title: "Betaling",
    items: [
      { q: "Hvornår bliver pengene trukket?", a: "Når du accepterer et bud, betaler du med det samme via Stripe. MEN pengene udbetales ikke til chaufføren endnu, de opbevares sikkert hos Grabber, indtil du har godkendt det udførte job. Det beskytter både dig og chaufføren." },
      { q: "Hvilke betalingsmetoder kan jeg bruge?", a: "Alle almindelige betalingskort (Visa, Mastercard m.fl.) samt Apple Pay og Link. Alle betalinger håndteres sikkert af Stripe, vi ser eller gemmer aldrig dine kortoplysninger." },
      { q: "Får jeg en kvittering?", a: "Ja! Du modtager automatisk en kvittering på email og som notifikation i appen, så snart betalingen er gennemført. Du kan også downloade en PDF-kvittering fra jobsiden, når jobbet er afsluttet." },
      { q: "Kan jeg annullere et job?", a: "Ja, så længe jobbet ikke er betalt, kan du annullere det direkte fra 'Mine opslag' eller jobsiden. Efter betaling kan jobbet ikke annulleres direkte, men du kan åbne en tvist, hvis noget går galt." },
    ],
  },
  {
    id: "tvister",
    icon: <Scale />,
    color: "bg-rose-50 text-rose-700",
    title: "Tvister & problemer",
    items: [
      { q: "Hvad gør jeg, hvis noget går galt?", a: "Åbn en tvist fra jobsiden, FØR du godkender jobbet. Beskriv problemet, vores team gennemgår sagen, inklusive chaufførens fotodokumentation, og finder en løsning." },
      { q: "Hvad sker der med mine penge under en tvist?", a: "Pengene forbliver parkeret sikkert hos Grabber, mens tvisten behandles. Chaufføren kan ikke få dem udbetalt, før sagen er afgjort." },
      { q: "Hvilke udfald kan en tvist have?", a: "Tre muligheder: Fuld refusion til dig, udbetaling til chaufføren (hvis jobbet er udført korrekt), eller en delt løsning. Afgørelsen baseres på dokumentation fra begge parter." },
      { q: "Hvorfor skal chaufføren uploade billeder?", a: "Chaufføren skal uploade billeder både ved start (varens stand ved afhentning) og ved aflevering. Det beskytter begge parter, hvis der senere opstår uenighed." },
    ],
  },
  {
    id: "udbetalinger",
    icon: <Wallet />,
    color: "bg-emerald-50 text-emerald-700",
    title: "Udbetalinger (for chauffører)",
    items: [
      { q: "Hvornår får jeg mine penge?", a: "I samme øjeblik kunden godkender jobbet, overføres din andel (80%) automatisk til din tilkoblede Stripe-konto, ingen ventetid, intet manuelt arbejde. Stripe udbetaler derefter til din bankkonto efter deres normale udbetalingsplan (typisk få hverdage)." },
      { q: "Hvordan tilkobler jeg min bankkonto?", a: "Gå til 'Mine indtægter' i menuen og klik 'Opret udbetalingskonto'. Du føres gennem Stripes sikre opsætning (tager ca. 3 minutter), helt samme proces som hos Uber og Wolt. Grabber ser aldrig dine bankoplysninger." },
      { q: "Hvad hvis kunden glemmer at godkende?", a: "Kunden får en påmindelse efter 24 timer. Har kunden ikke reageret efter 48 timer, godkendes jobbet automatisk, og du får din udbetaling." },
      { q: "Hvad tager Grabber i kommission?", a: "Grabber tager 20% af jobprisen. Du beholder 80%. Byder du fx 400 kr. på et job, får du 320 kr. udbetalt. Kommissionen dækker betalingshåndtering, platform og support." },
    ],
  },
  {
    id: "generelt",
    icon: <HelpCircle />,
    color: "bg-blue-50 text-blue-700",
    title: "Generelt",
    items: [
      { q: "Kan jeg dele mit job med andre?", a: "Ja! Brug 'Del'-knappen på jobsiden til at kopiere et link eller dele direkte via din telefon. Jo flere chauffører der ser dit job, jo hurtigere og billigere bud får du." },
      { q: "Hentes der kun med bil?", a: "Nej, mange af vores chauffører kører med ladcykel, især i byerne. Chaufførens transportmiddel fremgår af deres bud og profil, så du kan vælge den grønne løsning. 🚲" },
      { q: "Hvem kan blive chauffør (grabber)?", a: "Alle med et transportmiddel, ladcykel, personbil, trailer eller kassevogn. Opret en profil, angiv dit køretøj, og begynd at byde på jobs i dit område." },
      { q: "Er chaufførerne forsikrede?", a: "Chauffører er selvstændige og skal selv have relevant forsikring. Tjek chaufførens profil og anmeldelser fra tidligere kunder, før du accepterer et bud." },
    ],
  },
];

export default function FAQ() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Tilbage
      </Link>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">FAQ</p>
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Ofte stillede spørgsmål</h1>
        <p className="text-muted-foreground">Alt om betaling, tvister og udbetalinger, for både kunder og chauffører</p>
      </motion.div>

      <div className="space-y-8">
        {SECTIONS.map((s) => (
          <motion.div key={s.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-9 h-9 rounded-xl ${s.color} flex items-center justify-center [&>svg]:w-4.5 [&>svg]:h-4.5`}>{s.icon}</div>
              <h2 className="text-lg font-bold">{s.title}</h2>
            </div>
            <Card className="px-4" data-testid={`faq-section-${s.id}`}>
              <Accordion type="single" collapsible>
                {s.items.map((item, i) => (
                  <AccordionItem key={i} value={`${s.id}-${i}`} className={i === s.items.length - 1 ? "border-b-0" : ""}>
                    <AccordionTrigger className="text-sm font-semibold text-left hover:no-underline">{item.q}</AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Card>
          </motion.div>
        ))}
      </div>

      <Card className="mt-10 p-5 text-center bg-primary/5 border-primary/15">
        <p className="font-semibold mb-1">Fandt du ikke svar?</p>
        <p className="text-sm text-muted-foreground">Skriv til os på <a href="mailto:grabber2026@hotmail.com" className="text-primary hover:underline" data-testid="support-email-link">grabber2026@hotmail.com</a>, vi svarer typisk inden for 24 timer. Se også <Link to="/support" className="text-primary hover:underline">supportsiden</Link>.</p>
      </Card>
    </div>
  );
}
